import { memo, useRef, useEffect } from "react";
import { MicrophoneIcon } from "@heroicons/react/24/outline";
import { useIsMobile } from "../hooks";
import { useTranslation } from "react-i18next";

// Utility to check if the event contains files in clipboard
export const hasFileInClipboard = (e: React.ClipboardEvent): boolean => {
  return (
    e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0
  );
};

function calculateHeight(lines: number, textarea: HTMLTextAreaElement) {
  const style = window.getComputedStyle(textarea);
  const borderTopWidth = parseFloat(style.borderTopWidth);
  const borderBottomWidth = parseFloat(style.borderBottomWidth);
  const paddingTop = parseFloat(style.paddingTop);
  const paddingBottom = parseFloat(style.paddingBottom);
  const fontSize = parseFloat(style.fontSize);
  const lineHeight =
    style.lineHeight === "normal"
      ? 1.2 * fontSize
      : parseFloat(style.lineHeight);
  const lineTotalHeight =
    lines === 0 ? 0 : lineHeight * lines + paddingTop + paddingBottom;
  return Math.max(
    lineTotalHeight,
    textarea.scrollHeight + borderTopWidth + borderBottomWidth
  );
}

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isDisabled?: boolean;
  isSubmittable?: boolean;
  isSubmitDisabled?: boolean;
  placeholder?: string;
  onSwitchToAudio: () => void;
  onPasteFiles: (files: FileList) => void;
  isAudioDisabled?: boolean;
}

const ChatInput = memo(
  ({
    value,
    onChange,
    onSubmit,
    isDisabled,
    isSubmittable,
    isSubmitDisabled,
    placeholder,
    onSwitchToAudio,
    onPasteFiles,
    isAudioDisabled = false,
  }: ChatInputProps) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const isMobile = useIsMobile();

    const adjustHeight = () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${calculateHeight(1, textareaRef.current)}px`;
      }
    };

    useEffect(() => {
      adjustHeight();

      const observeTarget = textareaRef.current;
      if (observeTarget) {
        const resizeObserver = new ResizeObserver(adjustHeight);
        resizeObserver.observe(observeTarget);
        return () => resizeObserver.disconnect();
      }
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        if (isMobile) {
          return;
        } else {
          // On desktop: Enter sends the message (if not disabled and has content)
          if (!isSubmitDisabled && value.trim().length > 0) {
            e.preventDefault();
            onSubmit();
          }
        }
      }
    };

    // Handle paste events to capture images/files
    const handlePaste = (e: React.ClipboardEvent) => {
      if (hasFileInClipboard(e)) {
        onPasteFiles(e.clipboardData.files);
        e.preventDefault();
      }
    };

    useEffect(() => adjustHeight(), [value]);

    // Show mic button only when text input is empty
    const showMicButton = !isAudioDisabled && value.trim().length === 0;

    return (
      <div className="flex w-full">
        <div className="flex-grow flex flex-col">
          <textarea
            ref={textareaRef}
            disabled={isDisabled}
            rows={1}
            id="chat"
            aria-label="Chat input"
            tabIndex={0}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            className="w-full h-full max-h-52 resize-none py-2 text-lg leading-6 border-0 outline-none text-chat-text bg-transparent dark:bg-transparent ring-0 focus-visible:ring-0 disabled:cursor-not-allowed"
            maxLength={6000}
          />
        </div>

        <div className="flex items-center self-end ml-2">
          {showMicButton ? (
            <button
              type="button"
              onClick={onSwitchToAudio}
              disabled={isDisabled}
              className="p-2.5 text-chat-text hover:bg-slate-200 transition-colors rounded-full disabled:opacity-50"
              aria-label="Record audio"
            >
              <MicrophoneIcon className="h-5 w-5" />
            </button>
          ) : (
            <button
              disabled={!isSubmittable || isSubmitDisabled}
              type="button"
              aria-label="Submit Question"
              onClick={onSubmit}
              className="p-3 text-chat bg-brand hover:bg-brand-hover disabled:bg-transparent disabled:text-chat-text transition-colors disabled:opacity-40 rounded-full"
            >
              <span className="" data-state="closed">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="h-4 w-4"
                  strokeWidth="2"
                >
                  <path
                    d="M.5 1.163A1 1 0 0 1 1.97.28l12.868 6.837a1 1 0 0 1 0 1.766L1.969 15.72A1 1 0 0 1 .5 14.836V10.33a1 1 0 0 1 .816-.983L8.5 8 1.316 6.653A1 1 0 0 1 .5 5.67V1.163Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }
);

ChatInput.displayName = "ChatInput";

export default ChatInput;
